import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { api } from '../../../services/api';
import { useWorkspace } from '../WorkspaceContext';
import {
  addDays,
  addMonths,
  formatCurrency,
  formatDateKey,
  formatDateKeyFromMs,
  formatDayNumber,
  formatDisplayDate,
  formatDuration,
  formatMonthLabel,
  formatShortWeekday,
  formatTime,
  formatWeekLabel,
  monthGridDays,
  rangeBoundsMs,
  slotStartMs,
  startOfMonth,
  weekDays,
} from '../utils/datetime';

const emptyApptForm = {
  clientId: '',
  serviceId: '',
  staffId: '',
  startAt: '',
  notes: '',
};

const emptyBlockForm = {
  mode: 'day',
  startTime: '12:00',
  endTime: '13:00',
  reason: '',
};

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const Calendar = () => {
  const { activeLocation } = useWorkspace();
  const [view, setView] = useState('week');
  const [date, setDate] = useState(formatDateKey());
  const [appointments, setAppointments] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [form, setForm] = useState(emptyApptForm);
  const [blockForm, setBlockForm] = useState(emptyBlockForm);
  const [saving, setSaving] = useState(false);
  // { kind: 'appointment' | 'block', id }
  const [selectedEventKey, setSelectedEventKey] = useState(null);
  const dayDetailRef = useRef(null);
  const scrollToDayRef = useRef(false);

  const todayKey = formatDateKey();
  const activeServices = useMemo(
    () => services.filter((service) => service.active !== false),
    [services]
  );

  const visibleDays = useMemo(
    () => (view === 'week' ? weekDays(date) : monthGridDays(date)),
    [view, date]
  );

  const range = useMemo(() => {
    if (view === 'week') {
      const days = weekDays(date);
      return rangeBoundsMs(days[0], days[6]);
    }
    return rangeBoundsMs(visibleDays[0], visibleDays[visibleDays.length - 1]);
  }, [view, date, visibleDays]);

  const appointmentsByDay = useMemo(() => {
    const map = {};
    appointments.forEach((appt) => {
      const key = formatDateKeyFromMs(appt.startAt);
      if (!map[key]) map[key] = [];
      map[key].push(appt);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => a.startAt - b.startAt));
    return map;
  }, [appointments]);

  const blocksByDay = useMemo(() => {
    const map = {};
    blocks.forEach((block) => {
      const startKey = formatDateKeyFromMs(block.startAt);
      const endKey = formatDateKeyFromMs(Math.max(block.startAt, block.endAt - 1));
      let cursor = startKey;
      while (cursor <= endKey) {
        if (!map[cursor]) map[cursor] = [];
        map[cursor].push(block);
        cursor = addDays(cursor, 1);
      }
    });
    return map;
  }, [blocks]);

  const dayItems = useMemo(() => {
    const apptItems = (appointmentsByDay[date] || []).map((appt) => ({
      kind: 'appointment',
      sortAt: appt.startAt,
      data: appt,
    }));
    const blockItems = (blocksByDay[date] || []).map((block) => ({
      kind: 'block',
      sortAt: block.startAt,
      data: block,
    }));
    return [...apptItems, ...blockItems].sort((a, b) => a.sortAt - b.sortAt);
  }, [appointmentsByDay, blocksByDay, date]);

  const selectedEvent = useMemo(() => {
    if (!selectedEventKey) return null;
    if (selectedEventKey.kind === 'appointment') {
      const data = appointments.find((appt) => appt.id === selectedEventKey.id);
      return data ? { kind: 'appointment', data } : null;
    }
    const data = blocks.find((block) => block.id === selectedEventKey.id);
    return data ? { kind: 'block', data } : null;
  }, [selectedEventKey, appointments, blocks]);

  const heading = view === 'week' ? formatWeekLabel(date) : formatMonthLabel(date);
  const currentMonth = startOfMonth(date);

  const loadRange = useCallback(async () => {
    if (!activeLocation) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const locationId = activeLocation.id;
    const [apptResult, blockResult, clientResult, serviceResult, staffResult] = await Promise.all([
      api.getWorkspaceAppointments({ from: range.from, to: range.to, locationId }),
      api.getWorkspaceBlocks({ from: range.from, to: range.to, locationId }),
      api.getWorkspaceClients(locationId),
      api.getWorkspaceServices(locationId),
      api.getWorkspaceStaff(locationId),
    ]);

    if (
      apptResult.error ||
      blockResult.error ||
      clientResult.error ||
      serviceResult.error ||
      staffResult.error
    ) {
      setError(
        apptResult.error ||
          blockResult.error ||
          clientResult.error ||
          serviceResult.error ||
          staffResult.error ||
          'Failed to load calendar'
      );
    } else {
      setError('');
      setAppointments(Array.isArray(apptResult.data) ? apptResult.data : []);
      setBlocks(Array.isArray(blockResult.data) ? blockResult.data : []);
      setClients(Array.isArray(clientResult.data) ? clientResult.data : []);
      setServices(Array.isArray(serviceResult.data) ? serviceResult.data : []);
      setStaff(Array.isArray(staffResult.data) ? staffResult.data : []);
    }
    setLoading(false);
  }, [activeLocation, range.from, range.to]);

  useEffect(() => {
    loadRange();
  }, [loadRange]);

  useEffect(() => {
    if (selectedEventKey && !selectedEvent) {
      setSelectedEventKey(null);
    }
  }, [selectedEventKey, selectedEvent]);

  useEffect(() => {
    if (!formOpen || !form.serviceId) {
      setSlots([]);
      return;
    }

    let cancelled = false;
    (async () => {
      const result = await api.getWorkspaceAvailability({
        serviceId: form.serviceId,
        date,
        staffId: form.staffId || undefined,
        locationId: activeLocation?.id,
      });
      if (!cancelled) {
        setSlots(result.data?.slots || []);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formOpen, form.serviceId, form.staffId, date, activeLocation]);

  const openCreate = (dayKey = date) => {
    setDate(dayKey);
    setSelectedEventKey(null);
    setForm({
      ...emptyApptForm,
      serviceId: activeServices[0]?.id || '',
      staffId: staff[0]?.id || '',
      clientId: clients[0]?.id || '',
    });
    setFormOpen(true);
  };

  const openBlock = (dayKey = date) => {
    setDate(dayKey);
    setSelectedEventKey(null);
    setBlockForm(emptyBlockForm);
    setBlockOpen(true);
  };

  const handleDayClick = (dayKey) => {
    setSelectedEventKey(null);
    setFormOpen(false);
    if (dayKey === date) {
      dayDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    scrollToDayRef.current = true;
    setDate(dayKey);
  };

  useEffect(() => {
    if (!scrollToDayRef.current) return;
    scrollToDayRef.current = false;
    dayDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [date]);

  const openEvent = (kind, data, dayKey) => {
    if (dayKey) setDate(dayKey);
    setSelectedEventKey({ kind, id: data.id });
  };

  const closeEvent = () => setSelectedEventKey(null);

  const shiftRange = (direction) => {
    if (view === 'week') {
      setDate((prev) => addDays(prev, direction * 7));
    } else {
      setDate((prev) => addMonths(startOfMonth(prev), direction));
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!form.startAt) {
      setError('Choose an available time');
      return;
    }

    setSaving(true);
    setError('');
    const result = await api.createWorkspaceAppointment({
      clientId: form.clientId,
      serviceId: form.serviceId,
      staffId: form.staffId || null,
      startAt: Number(form.startAt),
      notes: form.notes.trim() || null,
      locationId: activeLocation?.id,
    });
    setSaving(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    setFormOpen(false);
    await loadRange();
  };

  const handleBlock = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    const payload =
      blockForm.mode === 'day'
        ? {
            allDay: true,
            date,
            reason: blockForm.reason.trim() || null,
            locationId: activeLocation?.id,
          }
        : {
            startAt: slotStartMs(date, blockForm.startTime),
            endAt: slotStartMs(date, blockForm.endTime),
            reason: blockForm.reason.trim() || null,
            locationId: activeLocation?.id,
          };

    if (blockForm.mode === 'range' && payload.endAt <= payload.startAt) {
      setSaving(false);
      setError('End time must be after start time');
      return;
    }

    const result = await api.createWorkspaceBlock(payload);
    setSaving(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    setBlockOpen(false);
    await loadRange();
  };

  const removeBlock = async (blockId) => {
    const result = await api.deleteWorkspaceBlock(blockId);
    if (result.error) {
      setError(result.error);
      return;
    }
    if (selectedEventKey?.kind === 'block' && selectedEventKey.id === blockId) {
      setSelectedEventKey(null);
    }
    await loadRange();
  };

  const updateStatus = async (appointmentId, status) => {
    const result = await api.updateWorkspaceAppointment(appointmentId, { status });
    if (result.error) {
      setError(result.error);
      return;
    }
    if (
      status === 'cancelled' &&
      selectedEventKey?.kind === 'appointment' &&
      selectedEventKey.id === appointmentId
    ) {
      setSelectedEventKey(null);
    }
    await loadRange();
  };

  const selectedAppointment =
    selectedEvent?.kind === 'appointment' ? selectedEvent.data : null;
  const selectedBlock = selectedEvent?.kind === 'block' ? selectedEvent.data : null;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.22em] text-saffron-600">Calendar</p>
          <h1 className="mt-2 font-display text-3xl font-light tracking-tight text-ink-900 sm:text-4xl">
            {heading}
          </h1>
          <p className="mt-2 text-sm text-ink-700/80">
            Open during{' '}
            <Link
              to="/app/manage/hours"
              className="underline decoration-saffron-500 decoration-2 underline-offset-4"
            >
              working hours
            </Link>
            . Click a day to see what’s booked, then add an appointment for that date.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex bg-sand-50 ring-1 ring-ink-900/10">
            {[
              { id: 'week', label: 'Week' },
              { id: 'month', label: 'Month' },
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setView(option.id)}
                className={`px-4 py-3 text-sm font-medium transition-colors ${
                  view === option.id
                    ? 'bg-ink-900 text-sand-50'
                    : 'text-ink-800 hover:bg-white'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="flex items-center bg-sand-50 ring-1 ring-ink-900/10">
            <button
              type="button"
              onClick={() => shiftRange(-1)}
              className="px-3 py-3 text-ink-800 hover:bg-white"
              aria-label={view === 'week' ? 'Previous week' : 'Previous month'}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setDate(formatDateKey())}
              className="px-3 py-3 text-sm font-medium text-ink-800 hover:bg-white"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => shiftRange(1)}
              className="px-3 py-3 text-ink-800 hover:bg-white"
              aria-label={view === 'week' ? 'Next week' : 'Next month'}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => openBlock()}
            className="inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 transition-colors hover:bg-white"
          >
            <Ban className="h-4 w-4" />
            Block
          </button>
          <button
            type="button"
            onClick={() => openCreate()}
            className="inline-flex items-center gap-2 bg-saffron-500 px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
          >
            <Plus className="h-4 w-4" />
            Book
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-6 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mt-8 bg-sand-50 ring-1 ring-ink-900/10">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-900/20 border-t-saffron-500" />
          </div>
        ) : view === 'week' ? (
          <div className="overflow-x-auto">
            <div className="grid min-w-[720px] grid-cols-7 divide-x divide-ink-900/10">
              {visibleDays.map((dayKey) => {
                const isSelected = dayKey === date;
                const isToday = dayKey === todayKey;
                const dayAppts = appointmentsByDay[dayKey] || [];
                const dayBlocks = blocksByDay[dayKey] || [];
                return (
                  <div
                    key={dayKey}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleDayClick(dayKey)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDayClick(dayKey);
                      }
                    }}
                    className={`min-h-[220px] cursor-pointer px-2 py-3 text-left transition-colors hover:bg-white ${
                      isSelected ? 'bg-white' : ''
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-1 px-1">
                      <span className="text-[11px] uppercase tracking-[0.14em] text-ink-600/60">
                        {formatShortWeekday(dayKey)}
                      </span>
                      <span
                        className={`flex h-7 w-7 items-center justify-center text-sm tabular-nums ${
                          isToday
                            ? 'bg-saffron-500 font-semibold text-ink-900'
                            : isSelected
                              ? 'bg-ink-900 text-sand-50'
                              : 'text-ink-900'
                        }`}
                      >
                        {formatDayNumber(dayKey)}
                      </span>
                    </div>
                    <div className="mt-3 space-y-1.5">
                      {dayBlocks.slice(0, 1).map((block) => (
                        <div
                          key={block.id}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEvent('block', block, dayKey);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              openEvent('block', block, dayKey);
                            }
                          }}
                          className="truncate bg-ink-900/10 px-1.5 py-1 text-[11px] text-ink-700 hover:bg-ink-900/20"
                        >
                          Blocked{block.reason ? ` · ${block.reason}` : ''}
                        </div>
                      ))}
                      {dayAppts.slice(0, 4).map((appt) => (
                        <div
                          key={appt.id}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEvent('appointment', appt, dayKey);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              openEvent('appointment', appt, dayKey);
                            }
                          }}
                          className={`truncate px-1.5 py-1 text-[11px] hover:opacity-90 ${
                            selectedEventKey?.id === appt.id
                              ? 'bg-saffron-500 text-ink-900'
                              : 'bg-ink-900 text-sand-50'
                          }`}
                        >
                          {formatTime(appt.startAt)} {appt.client?.name || 'Client'}
                        </div>
                      ))}
                      {dayAppts.length > 4 && (
                        <p className="px-1 text-[11px] text-ink-600/70">
                          +{dayAppts.length - 4} more
                        </p>
                      )}
                      {dayAppts.length === 0 && dayBlocks.length === 0 && (
                        <p className="px-1 pt-6 text-center text-[11px] text-ink-600/45">
                          Click to view
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-7 border-b border-ink-900/10">
              {WEEKDAY_LABELS.map((label) => (
                <div
                  key={label}
                  className="px-2 py-3 text-center text-[11px] uppercase tracking-[0.14em] text-ink-600/60"
                >
                  {label}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 auto-rows-fr">
              {visibleDays.map((dayKey) => {
                const inMonth = startOfMonth(dayKey) === currentMonth;
                const isSelected = dayKey === date;
                const isToday = dayKey === todayKey;
                const dayAppts = appointmentsByDay[dayKey] || [];
                const dayBlocks = blocksByDay[dayKey] || [];
                return (
                  <div
                    key={dayKey}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleDayClick(dayKey)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDayClick(dayKey);
                      }
                    }}
                    className={`min-h-[96px] cursor-pointer border-b border-r border-ink-900/10 px-2 py-2 text-left transition-colors hover:bg-white sm:min-h-[120px] ${
                      isSelected ? 'bg-white' : inMonth ? 'bg-sand-50' : 'bg-sand-100/70'
                    }`}
                  >
                    <span
                      className={`inline-flex h-7 w-7 items-center justify-center text-sm tabular-nums ${
                        isToday
                          ? 'bg-saffron-500 font-semibold text-ink-900'
                          : isSelected
                            ? 'bg-ink-900 text-sand-50'
                            : inMonth
                              ? 'text-ink-900'
                              : 'text-ink-600/40'
                      }`}
                    >
                      {formatDayNumber(dayKey)}
                    </span>
                    <div className="mt-1.5 space-y-1">
                      {dayBlocks.length > 0 && (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEvent('block', dayBlocks[0], dayKey);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              openEvent('block', dayBlocks[0], dayKey);
                            }
                          }}
                          className="truncate bg-ink-900/10 px-1 py-0.5 text-[10px] text-ink-700 hover:bg-ink-900/20"
                        >
                          Blocked
                        </div>
                      )}
                      {dayAppts.slice(0, 2).map((appt) => (
                        <div
                          key={appt.id}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEvent('appointment', appt, dayKey);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              openEvent('appointment', appt, dayKey);
                            }
                          }}
                          className={`truncate px-1 py-0.5 text-[10px] hover:opacity-90 ${
                            selectedEventKey?.id === appt.id
                              ? 'bg-saffron-500 text-ink-900'
                              : 'bg-ink-900 text-sand-50'
                          }`}
                        >
                          {formatTime(appt.startAt)} {appt.client?.name || 'Client'}
                        </div>
                      ))}
                      {dayAppts.length > 2 && (
                        <p className="text-[10px] text-ink-600/70">+{dayAppts.length - 2}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Selected day detail */}
      <div ref={dayDetailRef} className="mt-8 scroll-mt-28 sm:scroll-mt-32">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-ink-600/60">Selected day</p>
            <h2 className="mt-1 font-display text-2xl font-light text-ink-900">
              {formatDisplayDate(date)}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => openBlock(date)}
              className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 hover:bg-white"
            >
              Block day
            </button>
            <button
              type="button"
              onClick={() => openCreate(date)}
              className="inline-flex items-center gap-2 bg-saffron-500 px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 hover:bg-saffron-400"
            >
              <Plus className="h-3.5 w-3.5" />
              Add appointment for this day
            </button>
          </div>
        </div>

        <div className="mt-4 bg-sand-50 ring-1 ring-ink-900/10">
          {dayItems.length === 0 ? (
            <div className="px-6 py-14 text-center">
              <p className="font-display text-2xl font-light text-ink-900">No appointments yet</p>
              <p className="mt-2 text-sm text-ink-700/70">Nothing is booked on this day.</p>
              <button
                type="button"
                onClick={() => openCreate(date)}
                className="mt-6 inline-flex items-center gap-2 bg-saffron-500 px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
              >
                <Plus className="h-4 w-4" />
                Add appointment for this day
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-ink-900/10">
              {dayItems.map((item) => {
                if (item.kind === 'block') {
                  const block = item.data;
                  const isOpen = selectedEventKey?.kind === 'block' && selectedEventKey.id === block.id;
                  return (
                    <li key={`block-${block.id}`}>
                      <button
                        type="button"
                        onClick={() => openEvent('block', block)}
                        className={`flex w-full items-start gap-4 px-5 py-5 text-left transition-colors hover:bg-white ${
                          isOpen ? 'bg-white' : ''
                        }`}
                      >
                        <div className="flex-none bg-ink-900/10 px-3 py-2 text-center text-ink-800 ring-1 ring-ink-900/15">
                          {block.allDay ? (
                            <p className="text-sm font-semibold">All day</p>
                          ) : (
                            <>
                              <p className="text-sm font-semibold tabular-nums">
                                {formatTime(block.startAt)}
                              </p>
                              <p className="text-[11px] text-ink-600/70">{formatTime(block.endAt)}</p>
                            </>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-ink-900">Blocked</p>
                          <p className="mt-1 text-sm text-ink-700/80">
                            {block.reason || 'Closed by owner'}
                          </p>
                        </div>
                      </button>
                    </li>
                  );
                }

                const appt = item.data;
                const isOpen =
                  selectedEventKey?.kind === 'appointment' && selectedEventKey.id === appt.id;
                return (
                  <li key={appt.id}>
                    <button
                      type="button"
                      onClick={() => openEvent('appointment', appt)}
                      className={`flex w-full items-start gap-4 px-5 py-5 text-left transition-colors hover:bg-white ${
                        isOpen ? 'bg-white' : ''
                      }`}
                    >
                      <div className="flex-none bg-ink-900 px-3 py-2 text-center text-sand-50">
                        <p className="text-sm font-semibold tabular-nums">{formatTime(appt.startAt)}</p>
                        <p className="text-[11px] text-sand-300">{formatTime(appt.endAt)}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-ink-900">
                          {appt.client?.name || 'Client'}
                        </p>
                        <p className="mt-1 text-sm text-ink-700/80">
                          {appt.service?.name || 'Service'}
                          {appt.service?.durationMinutes
                            ? ` · ${formatDuration(appt.service.durationMinutes)}`
                            : ''}
                          {appt.staff?.name ? ` · ${appt.staff.name}` : ''}
                        </p>
                        <p className="mt-1 text-xs uppercase tracking-[0.14em] text-ink-600/60">
                          {appt.status?.replace('_', ' ')} ·{' '}
                          {appt.source === 'booking_link' ? 'Online' : 'Manual'}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {dayItems.length > 0 && (
            <div className="border-t border-ink-900/10 px-5 py-4">
              <button
                type="button"
                onClick={() => openCreate(date)}
                className="inline-flex w-full items-center justify-center gap-2 bg-saffron-500 px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
              >
                <Plus className="h-4 w-4" />
                Add appointment for this day
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedEvent && (
        <>
          <button
            type="button"
            aria-label="Close details"
            onClick={closeEvent}
            className="fixed inset-0 z-40 bg-ink-950/35"
          />
          <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-sand-50 shadow-[-20px_0_50px_-30px_rgba(6,18,15,0.45)] ring-1 ring-ink-900/10">
            <div className="flex items-start justify-between gap-4 border-b border-ink-900/10 px-5 py-5 sm:px-6">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-saffron-600">
                  {selectedAppointment ? 'Appointment' : 'Blocked time'}
                </p>
                <h2 className="mt-2 font-display text-2xl font-light text-ink-900">
                  {selectedAppointment
                    ? selectedAppointment.client?.name || 'Client'
                    : 'Blocked'}
                </h2>
              </div>
              <button type="button" onClick={closeEvent} aria-label="Close">
                <X className="h-5 w-5 text-ink-700" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-6 sm:px-6">
              {selectedAppointment && (
                <div className="space-y-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-600/60">
                      When
                    </p>
                    <p className="mt-2 text-sm text-ink-900">
                      {formatDisplayDate(formatDateKeyFromMs(selectedAppointment.startAt))}
                    </p>
                    <p className="mt-1 font-display text-xl font-light text-ink-900">
                      {formatTime(selectedAppointment.startAt)} –{' '}
                      {formatTime(selectedAppointment.endAt)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-600/60">
                      Client
                    </p>
                    <p className="mt-2 font-medium text-ink-900">
                      {selectedAppointment.client?.name || '—'}
                    </p>
                    {selectedAppointment.client?.phone && (
                      <p className="mt-1 text-sm text-ink-700/80">
                        {selectedAppointment.client.phone}
                      </p>
                    )}
                    {selectedAppointment.client?.email && (
                      <p className="mt-1 text-sm text-ink-700/80">
                        {selectedAppointment.client.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-600/60">
                      Service
                    </p>
                    <p className="mt-2 font-medium text-ink-900">
                      {selectedAppointment.service?.name || '—'}
                    </p>
                    <p className="mt-1 text-sm text-ink-700/80">
                      {selectedAppointment.service?.durationMinutes
                        ? formatDuration(selectedAppointment.service.durationMinutes)
                        : '—'}
                      {selectedAppointment.staff?.name
                        ? ` · ${selectedAppointment.staff.name}`
                        : ''}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-600/60">
                      Payment
                    </p>
                    <p className="mt-2 font-display text-2xl font-light text-ink-900">
                      {formatCurrency(
                        selectedAppointment.price ?? selectedAppointment.service?.price,
                        selectedAppointment.currency ||
                          selectedAppointment.service?.currency ||
                          'NGN'
                      )}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-600/60">
                        Status
                      </p>
                      <p className="mt-2 text-sm capitalize text-ink-900">
                        {selectedAppointment.status?.replace('_', ' ') || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-600/60">
                        Source
                      </p>
                      <p className="mt-2 text-sm text-ink-900">
                        {selectedAppointment.source === 'booking_link' ? 'Online booking' : 'Manual'}
                      </p>
                    </div>
                  </div>

                  {selectedAppointment.notes && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-600/60">
                        Notes
                      </p>
                      <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ink-800">
                        {selectedAppointment.notes}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {selectedBlock && (
                <div className="space-y-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-600/60">
                      When
                    </p>
                    <p className="mt-2 text-sm text-ink-900">
                      {formatDisplayDate(formatDateKeyFromMs(selectedBlock.startAt))}
                    </p>
                    <p className="mt-1 font-display text-xl font-light text-ink-900">
                      {selectedBlock.allDay
                        ? 'All day'
                        : `${formatTime(selectedBlock.startAt)} – ${formatTime(selectedBlock.endAt)}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-600/60">
                      Reason
                    </p>
                    <p className="mt-2 text-sm text-ink-800">
                      {selectedBlock.reason || 'Closed by owner'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-ink-900/10 px-5 py-4 sm:px-6">
              {selectedAppointment?.status === 'confirmed' && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => updateStatus(selectedAppointment.id, 'arrived')}
                    className="px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 hover:bg-white"
                  >
                    Arrived
                  </button>
                  <button
                    type="button"
                    onClick={() => updateStatus(selectedAppointment.id, 'completed')}
                    className="px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 hover:bg-white"
                  >
                    Complete
                  </button>
                  <button
                    type="button"
                    onClick={() => updateStatus(selectedAppointment.id, 'cancelled')}
                    className="px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-red-700 ring-1 ring-red-200 hover:bg-red-50"
                  >
                    Cancel
                  </button>
                </div>
              )}
              {selectedAppointment?.status === 'arrived' && (
                <button
                  type="button"
                  onClick={() => updateStatus(selectedAppointment.id, 'completed')}
                  className="w-full bg-saffron-500 px-3 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 hover:bg-saffron-400"
                >
                  Complete
                </button>
              )}
              {selectedBlock && (
                <button
                  type="button"
                  onClick={() => removeBlock(selectedBlock.id)}
                  className="w-full px-3 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-red-700 ring-1 ring-red-200 hover:bg-red-50"
                >
                  Unblock
                </button>
              )}
            </div>
          </aside>
        </>
      )}

      {blockOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-ink-950/50 p-3 sm:items-center sm:p-4">
          <div className="w-full max-w-md bg-sand-50 p-5 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.55)] ring-1 ring-ink-900/10 sm:p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-light text-ink-900">Block time</h2>
              <button type="button" onClick={() => setBlockOpen(false)} aria-label="Close">
                <X className="h-5 w-5 text-ink-700" />
              </button>
            </div>
            <p className="mt-2 text-sm text-ink-700/80">
              Blocked time is closed for online booking on {formatDisplayDate(date)}.
            </p>

            <form className="mt-6 space-y-4" onSubmit={handleBlock}>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'day', label: 'Whole day' },
                  { id: 'range', label: 'Time range' },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setBlockForm((prev) => ({ ...prev, mode: option.id }))}
                    className={`px-3 py-3 text-sm ${
                      blockForm.mode === option.id
                        ? 'bg-ink-900 text-sand-50'
                        : 'bg-white text-ink-800 ring-1 ring-ink-900/10'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {blockForm.mode === 'range' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                      From
                    </label>
                    <input
                      type="time"
                      required
                      value={blockForm.startTime}
                      onChange={(e) =>
                        setBlockForm((prev) => ({ ...prev, startTime: e.target.value }))
                      }
                      className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                      To
                    </label>
                    <input
                      type="time"
                      required
                      value={blockForm.endTime}
                      onChange={(e) =>
                        setBlockForm((prev) => ({ ...prev, endTime: e.target.value }))
                      }
                      className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Reason
                </label>
                <input
                  value={blockForm.reason}
                  onChange={(e) => setBlockForm((prev) => ({ ...prev, reason: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  placeholder="Lunch / holiday / training"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-saffron-500 px-5 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
              >
                {saving ? 'Blocking…' : 'Block time'}
              </button>
            </form>
          </div>
        </div>
      )}

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-ink-950/50 p-3 sm:items-center sm:p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto bg-sand-50 p-5 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.55)] ring-1 ring-ink-900/10 sm:p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-light text-ink-900">New appointment</h2>
              <button type="button" onClick={() => setFormOpen(false)} aria-label="Close">
                <X className="h-5 w-5 text-ink-700" />
              </button>
            </div>
            <p className="mt-2 text-sm text-ink-700/80">{formatDisplayDate(date)}</p>

            <form className="mt-6 space-y-4" onSubmit={handleCreate}>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Client
                </label>
                <select
                  required
                  value={form.clientId}
                  onChange={(e) => setForm((prev) => ({ ...prev, clientId: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                >
                  <option value="">Select client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
                {clients.length === 0 && (
                  <p className="mt-2 text-xs text-ink-600/70">
                    Add a client in the Clients tab first.
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Service
                </label>
                <select
                  required
                  value={form.serviceId}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, serviceId: e.target.value, startAt: '' }))
                  }
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                >
                  <option value="">Select service</option>
                  {activeServices.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name} · {formatDuration(service.durationMinutes)} ·{' '}
                      {formatCurrency(service.price, service.currency)}
                    </option>
                  ))}
                </select>
              </div>

              {staff.length > 1 && (
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                    Staff
                  </label>
                  <select
                    value={form.staffId}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, staffId: e.target.value, startAt: '' }))
                    }
                    className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  >
                    {staff.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Available times · {formatDisplayDate(date)}
                </p>
                {!form.serviceId ? (
                  <p className="text-sm text-ink-600/70">Choose a service to see open slots.</p>
                ) : slots.length === 0 ? (
                  <p className="text-sm text-ink-600/70">No open slots on this day.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {slots.map((slot) => {
                      const selected = String(form.startAt) === String(slot.startAt);
                      return (
                        <button
                          key={slot.startAt}
                          type="button"
                          onClick={() =>
                            setForm((prev) => ({ ...prev, startAt: String(slot.startAt) }))
                          }
                          className={`px-2 py-2.5 text-center text-sm ${
                            selected
                              ? 'bg-saffron-500 text-ink-900'
                              : 'bg-white text-ink-800 ring-1 ring-ink-900/10 hover:bg-ink-900 hover:text-sand-50'
                          }`}
                        >
                          {slot.time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Notes
                </label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                />
              </div>

              <button
                type="submit"
                disabled={saving || !form.clientId || !form.serviceId || !form.startAt}
                className="w-full bg-saffron-500 px-5 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
              >
                {saving ? 'Booking…' : 'Confirm appointment'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
